import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import type { BlockAction, ICallToActionBlockProps } from "./CallToActionBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

const getSafeRel = (target?: string) => (target === "_blank" ? "noopener noreferrer" : undefined);

const getImageAlt = (image: NonNullable<ICallToActionBlockProps["image"]>) => image.alternateText;

const getObjectPosition = (image: NonNullable<ICallToActionBlockProps["image"]>) =>
  image.focalPoint ? `${image.focalPoint.x}% ${image.focalPoint.y}%` : "center";

const ActionLink = ({ button, inverted = false }: { button: BlockAction; inverted?: boolean }) => {
  const Link = resolveKaajPrimitive("Link");
  const style = button.style;
  const isLink = style === "link";
  const isPrimary = style === "primary";
  const isSecondary = style === "secondary" || style === "outline" || style === "ghost";

  return (
    <Link
      aria-label={button.ariaLabel}
      className={cn(
        "inline-flex min-h-11 items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition-[background-color,border-color,box-shadow,color,transform] duration-200 motion-reduce:transition-none max-[420px]:w-full",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4",
        inverted ? "focus-visible:outline-white" : "focus-visible:outline-[#2144FF]",
        isLink &&
          cn(
            "min-h-0 rounded-none px-0 py-0 underline decoration-current/25 underline-offset-4 hover:decoration-current max-[420px]:w-auto",
            inverted ? "text-white hover:text-[#F6F1FF]" : "text-[#2144FF] hover:text-[#132EDB]",
          ),
        isPrimary &&
          (inverted
            ? "bg-white text-[#2144FF] shadow-[0_18px_42px_rgba(21,25,39,0.18)] hover:bg-[#F6F1FF] motion-safe:hover:-translate-y-0.5"
            : "bg-[#2144FF] text-white shadow-[0_14px_32px_rgba(33,68,255,0.28)] hover:bg-[#132EDB] motion-safe:hover:-translate-y-0.5"),
        !isLink &&
          isSecondary &&
          (inverted
            ? "border border-white/34 bg-white/10 text-white hover:border-white/60 hover:bg-white/16 hover:shadow-[0_14px_34px_rgba(21,25,39,0.12)]"
            : "border border-[#D9E1FF] bg-white text-[#2144FF] shadow-[0_10px_24px_rgba(33,68,255,0.1)] hover:border-[#2144FF] hover:bg-[#F6F1FF]"),
      )}
      href={button.href}
      rel={getSafeRel(button.target)}
      target={button.target}
    >
      {button.text}
      {isPrimary ? (
        <span aria-hidden="true" className="ml-2">
          →
        </span>
      ) : null}
    </Link>
  );
};

const CtaImage = ({
  compact = false,
  eager = false,
  elevated = false,
  image,
}: {
  compact?: boolean;
  eager?: boolean;
  elevated?: boolean;
  image: ICallToActionBlockProps["image"];
}) => {
  if (!image?.defaultSrc) {
    return null;
  }

  const Image = resolveKaajPrimitive("Image");
  const hasCaption = Boolean(image.caption);

  return (
    <figure
      className={cn(
        "relative overflow-hidden rounded-[1.5rem] border p-2",
        elevated
          ? "border-white/45 bg-white/12 shadow-[0_24px_70px_rgba(21,25,39,0.2)]"
          : "border-[#D9E1FF] bg-white shadow-[0_20px_58px_rgba(33,68,255,0.11)]",
        compact ? "mx-auto" : "w-full",
      )}
    >
      <div className="relative overflow-hidden rounded-[1.15rem] bg-[#EEF2FF]">
        <Image
          alt={getImageAlt(image)}
          className={cn("w-full object-cover", compact ? "aspect-[16/9]" : "aspect-[14/9]")}
          decoding="async"
          height={image.height ?? undefined}
          loading={compact || eager || elevated ? "eager" : "lazy"}
          priority={compact || eager || elevated}
          sizes={
            image.sizes ??
            (compact
              ? "(min-width: 1024px) 50rem, 92vw"
              : "(min-width: 1280px) 28rem, (min-width: 1024px) 38vw, 92vw")
          }
          src={image.defaultSrc}
          srcSet={image.srcSet ?? undefined}
          style={{ objectPosition: getObjectPosition(image) }}
          width={image.width ?? undefined}
        />
      </div>
      {hasCaption ? (
        <figcaption
          className={cn(
            "relative mt-3 rounded-[1rem] px-4 py-3 text-sm leading-6",
            elevated
              ? "bg-white/12 text-[#E8EEFF] ring-1 ring-white/16"
              : "bg-[#FAFBFF] text-[#405076] ring-1 ring-[#D9E1FF]",
          )}
        >
          {image.caption ? <p>{image.caption}</p> : null}
        </figcaption>
      ) : null}
    </figure>
  );
};

export const CallToActionBlock = ({
  anchorId,
  buttons,
  direction,
  eyebrow,
  heading,
  image,
  parentClass,
  spacing,
  subheading,
  variant,
}: ICallToActionBlockProps) => {
  const isHorizontal = variant === "horizontal";
  const hasImage = Boolean(image?.defaultSrc);
  const visibleButtons = buttons ?? [];
  const hasContent = Boolean(eyebrow || heading || subheading || visibleButtons.length);
  const blockId = anchorId;
  const headingId = blockId && heading ? `${blockId}-heading` : undefined;
  const useBlueStrip = false;

  if (!hasContent && !hasImage) {
    return null;
  }

  const copy = hasContent ? (
    <div
      className={cn(
        "w-full min-w-0",
        isHorizontal ? "max-w-none text-left" : "mx-auto max-w-3xl text-center",
      )}
    >
      {eyebrow ? (
        <p
          className={cn(
            "mb-4 inline-flex max-w-full rounded-full border px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] wrap-anywhere",
            "border-[#D9E1FF] bg-[#F6F1FF] text-[#2144FF]",
          )}
        >
          {eyebrow}
        </p>
      ) : null}
      {heading ? (
        <h2 className="kaaj-section-heading text-[#151927]" id={headingId}>
          {heading}
        </h2>
      ) : null}
      {subheading ? (
        <RichTextHtml
          className={cn(
            "mt-5 text-pretty text-base leading-8 wrap-anywhere sm:text-lg",
            isHorizontal && !hasImage ? "max-w-none" : "",
            isHorizontal ? undefined : "mx-auto",
            "text-[#405076]",
          )}
          html={subheading}
        />
      ) : null}
    </div>
  ) : null;

  const actionList = visibleButtons.length ? (
    <div
      className={cn(
        "mt-8 flex flex-wrap items-center gap-3 max-[420px]:flex-col max-[420px]:items-stretch",
        isHorizontal ? "justify-start" : "justify-center",
      )}
    >
      {visibleButtons.map((button, index) => (
        <ActionLink
          button={button}
          inverted={useBlueStrip}
          key={`${button.href}-${button.text}-${index}`}
        />
      ))}
    </div>
  ) : null;

  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        "relative isolate overflow-hidden",
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      id={blockId ?? undefined}
    >
      <div className="kaaj-container">
        <div
          className={cn(
            "relative overflow-hidden rounded-[1.75rem] border p-6 shadow-[0_24px_70px_rgba(33,68,255,0.11)] sm:p-8 lg:rounded-[2rem] lg:p-10 xl:p-12",
            "border-[#D9E1FF] bg-[linear-gradient(135deg,#FFFFFF_0%,#FAFBFF_58%,#F6F1FF_100%)] text-[#151927]",
          )}
        >
          {isHorizontal ? (
            <div
              className={cn(
                "relative grid w-full min-w-0 items-center gap-8 lg:gap-12",
                hasImage ? "lg:grid-cols-2" : "lg:grid-cols-1",
                direction === "ltr" &&
                  hasImage &&
                  "lg:[&>figure]:order-first lg:[&>div:first-child]:order-last",
              )}
            >
              <div className="w-full min-w-0">
                {copy}
                {actionList}
              </div>
              {hasImage ? <CtaImage eager elevated={useBlueStrip} image={image} /> : null}
            </div>
          ) : (
            <div className="relative mx-auto max-w-[58rem]">
              {copy}
              {actionList}
              {hasImage ? (
                <div className={cn("mx-auto", hasContent ? "mt-10 sm:mt-12" : undefined)}>
                  {hasContent ? (
                    <div
                      aria-hidden="true"
                      className={cn(
                        "mx-auto mb-7 h-px max-w-2xl bg-gradient-to-r from-transparent to-transparent",
                        "via-[#C7D4FF]",
                      )}
                    />
                  ) : null}
                  <CtaImage compact elevated={useBlueStrip} image={image} />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

CallToActionBlock.displayName = "Block::CallToActionBlock";
